var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};

import logger from '../util/logger';
import { getUserMedia, getMediaConstraints, sdpStereoHack, sdpBitrateHack } from './helpers';
import { SwEvent } from '../util/constants';
import { PeerType } from './constants';
import { attachMediaStream, muteMediaElement, sdpToJsonHack, RTCPeerConnection, streamIsValid } from '../util/webrtc';
import { isFunction } from '../util/helpers';
import { trigger } from '../services/Handler';
export default class Peer {
  constructor(type, options) {
    this.type = type;
    this.options = options;
    this.onSdpReadyTwice = null;
    this._negotiating = false;
    logger.info('New Peer with type:', this.type, 'Options:', this.options);
    this._constraints = {
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    };
    this._sdpReady = this._sdpReady.bind(this);

    this._init();
  }

  startNegotiation() {
    this._negotiating = true;

    if (this._isOffer()) {
      this._createOffer();
    } else {
      this._createAnswer();
    }
  }

  _init() {
    return __awaiter(this, void 0, void 0, function* () {
      this.instance = RTCPeerConnection(this._config());

      this.instance.onsignalingstatechange = event => {
        switch (this.instance.signalingState) {
          case 'stable':
            this._negotiating = false;
            break;

          case 'closed':
            this.instance = null;
            break;

          default:
            this._negotiating = true;
        }
      };

      this.instance.onnegotiationneeded = event => {
        if (this._negotiating) {
          logger.debug('Skip twice onnegotiationneeded..');
          return;
        }

        this.startNegotiation();
      };

      this.options.localStream = yield this._retrieveLocalStream().catch(error => {
        trigger(SwEvent.MediaError, error, this.options.id);
        return null;
      });
      const {
        localElement,
        localStream = null,
        screenShare = false
      } = this.options;

      if (streamIsValid(localStream)) {
        if (typeof this.instance.addTrack === 'function') {
          localStream.getTracks().forEach(t => this.instance.addTrack(t, localStream));
        } else {
          this.instance.addStream(localStream);
        }

        if (screenShare !== true) {
          muteMediaElement(localElement);
          attachMediaStream(localElement, localStream);
        }
      } else if (localStream === null) {
        this.startNegotiation();
      }
    });
  }

  _createOffer() {
    if (!this._isOffer()) {
      return;
    }

    this.instance.createOffer(this._constraints).then(this._setLocalDescription.bind(this)).then(this._sdpReady).catch(error => logger.error('Peer _createOffer error:', error));
  }

  _createAnswer() {
    if (!this._isAnswer()) {
      return;
    }

    const {
      remoteSdp,
      useStereo
    } = this.options;
    const sdp = useStereo ? sdpStereoHack(remoteSdp) : remoteSdp;
    const sessionDescr = sdpToJsonHack({
      sdp,
      type: PeerType.Offer
    });
    this.instance.setRemoteDescription(sessionDescr).then(() => this.instance.createAnswer()).then(this._setLocalDescription.bind(this)).then(this._sdpReady).catch(error => logger.error('Peer _createAnswer error:', error));
  }

  _setLocalDescription(sessionDescription) {
    const {
      useStereo,
      googleMaxBitrate,
      googleMinBitrate,
      googleStartBitrate
    } = this.options;

    if (useStereo) {
      sessionDescription.sdp = sdpStereoHack(sessionDescription.sdp);
    }

    if (googleMaxBitrate && googleMinBitrate && googleStartBitrate) {
      sessionDescription.sdp = sdpBitrateHack(sessionDescription.sdp, googleMaxBitrate, googleMinBitrate, googleStartBitrate);
    }

    return this.instance.setLocalDescription(sessionDescription);
  }

  _sdpReady() {
    if (isFunction(this.onSdpReadyTwice)) {
      this.onSdpReadyTwice(this.instance.localDescription);
    }
  }

  _retrieveLocalStream() {
    return __awaiter(this, void 0, void 0, function* () {
      if (streamIsValid(this.options.localStream)) {
        return this.options.localStream;
      }

      const constraints = yield getMediaConstraints(this.options);
      return getUserMedia(constraints);
    });
  }

  _isOffer() {
    return this.type === PeerType.Offer;
  }

  _isAnswer() {
    return this.type === PeerType.Answer;
  }

  _config() {
    const {
      iceServers = []
    } = this.options;
    const config = {
      sdpSemantics: 'unified-plan',
      bundlePolicy: 'max-compat',
      iceServers
    };
    logger.info('RTC config', config);
    return config;
  }

}